"use client";

import clsx from "clsx";
import { useSearchParams } from "next/navigation";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { v7 as uuidV7 } from "uuid";
import AskBarChart from "@/components/ask/AskBarChart";
import AskChatBubble from "@/components/ask/AskChatBubble";
import AskForm from "@/components/ask/AskForm";
import AskPieChart from "@/components/ask/AskPieChart";
import AskText from "@/components/ask/AskText";
import AskUserInput from "@/components/ask/AskUserInput";
import Button from "@/components/library/Button";
import { BRANDS } from "@/enums/brands";
import { THEME } from "@/enums/profile";
import { useAlert } from "@/hooks/useAlert";
import { useProfile } from "@/hooks/useProfile";
import { GptDetails } from "@/types/chat";
import { GetAssistantChatChannel } from "@/utils/chat";
import { GetTextFromLangString } from "@/utils/common/translations";
import { FileUploadTypes } from "@/utils/files";
import { WingmanClient } from "@lula-technologies-inc/wingman";
import { EventStream } from "@lula-technologies-inc/wingman/dist/commonjs/lib/event-streams";
import {
  ConversationMessage,
  StreamingConversationMessage,
} from "@lula-technologies-inc/wingman/dist/commonjs/models/components";

const client = new WingmanClient({
  serverURL: process.env.NEXT_PUBLIC_WINGMAN_URL!,
});

type ConversationMessageContent = ConversationMessage["contents"];

const CleanupMessages = (
  msgs: ConversationMessageContent
): ConversationMessageContent => {
  const cleanedMessages = msgs.map((msg) => {
    if (msg.type === "text") {
      return msg.content.length > 0 ? msg : null;
    } else if (msg.type === "function_result") {
      if (
        msg.functionName === "pie_chart" ||
        msg.functionName === "bar_chart"
      ) {
        return null;
      }
      return msg;
    }
    return msg;
  });

  return cleanedMessages.filter(
    (msg) => msg !== null
  ) as ConversationMessageContent;
};

export default function Page() {
  const searchParams = useSearchParams();
  const fixedParam = searchParams.get("fixed");
  const storeMessagesParam = searchParams.get("storeMessages");

  const fixed: boolean | undefined =
    fixedParam === "true" ? true : fixedParam === "false" ? false : undefined;
  const storeMessages: boolean = storeMessagesParam === "false" ? false : true;

  const { ui } = useProfile();
  const { setAlert } = useAlert();
  const { t } = useTranslation("ask");
  const { t: ct } = useTranslation("common");

  // Create a ref to cache input value across renders
  const cachedInputRef = useRef<string>("");

  const [welcomeMessage, setWelcomeMessage] = useState<string>("");
  const [details, setDetails] = useState<GptDetails | null>(null);
  const [conversationId, setConversationId] = useState<string>();
  const [hasExistingConversation, setHasExistingConversation] =
    useState<boolean>();
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingExisting, setIsLoadingExisting] = useState(false);
  const [isResponding, setIsResponding] = useState(false);

  const scrollableRef = useRef<HTMLDivElement>(null);

  // Create a wrapper for setInput to also update the cached value
  const setInputWithCache = (
    value: string | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    let newValue = "";
    if (typeof value === "string") {
      newValue = value;
    } else {
      newValue = value.target.value;
    }

    // Update the cached value
    cachedInputRef.current = newValue;

    // Update the state
    setInput(newValue);
  };

  // Always restore cached input on component mount
  useEffect(() => {
    if (cachedInputRef.current) {
      setInput(cachedInputRef.current);
    }
  }, []);

  const sendHeight = useCallback(() => {
    window.parent.postMessage(
      { height: document.documentElement.scrollHeight },
      "*"
    );
  }, []);

  const handleParentMessage = useCallback(
    async (event: MessageEvent) => {
      if (event.data && event.data.type === "applyMessage" && !isLoading) {
        const payload = event.data.payload;
        if (typeof payload === "string") {
          await appendMessage(payload);
        } else if (typeof payload === "object") {
          const { action, content } = payload as {
            action: "append" | "create" | "type";
            content: string;
          };

          if (action === "append") {
            await appendMessage(content);
          } else if (action === "create") {
            const newConversationId = await startNewConversation();

            if (content && content.length > 0) {
              await appendMessage(content, newConversationId);
            }
          } else if (action === "type") {
            setInput(content);
          }
        }
      }
    },
    [conversationId, details, isLoading]
  );

  useEffect(() => {
    window.addEventListener("message", handleParentMessage);
    return () => window.removeEventListener("message", handleParentMessage);
  }, [handleParentMessage]);

  useEffect(() => {
    if (fixed && isResponding && scrollableRef.current) {
      scrollableRef.current.scrollTop = scrollableRef.current.scrollHeight;
    }
  }, [isResponding, fixed, messages]);

  const fetchExistingConversation = async () => {
    if (!conversationId || !hasExistingConversation || !details) {
      return;
    }

    setIsLoadingExisting(true);

    const response = await client.conversations.listConversationMessages({
      xOrgId: details.orgId,
      conversationId,
    });

    setHasExistingConversation(false);
    setIsLoadingExisting(false);

    if (!response || response.length === 0) {
      setMessages([]);
      return;
    }

    setMessages(response);
  };

  const appendMessage = async (
    text: string,
    convId: string | undefined = conversationId
  ) => {
    if (!convId || !details) {
      console.error(
        "No conversation ID or details available",
        conversationId,
        convId,
        details
      );
      return;
    }

    // Set loading state to show indicator
    setIsLoading(true);

    // Add the user message to the UI immediately
    setMessages((prevMessages) => [
      ...prevMessages,
      {
        id: uuidV7(),
        contents: [
          {
            type: "text",
            content: text,
          },
        ],
        role: "user",
        author: "self",
        timestamp: new Date(),
      },
    ]);

    // Clear input
    setInputWithCache("");

    // Set responding state for scrolling
    setIsResponding(true);

    // Send the message to the API
    const response = await client.conversations.appendConversationMessage({
      xOrgId: details?.orgId!,
      conversationId: convId,
      appendConversationMessageRequest: {
        contents: [
          {
            type: "text",
            content: text,
          },
        ],
      },
    });

    // Process the response - this will update loading state when content starts streaming
    await processResponse(response);
    setIsResponding(false);
  };

  const appendFormResponse = async (
    callId: string,
    formData: Record<string, string | number>
  ): Promise<boolean> => {
    if (!conversationId || !details) {
      return false;
    }

    setIsLoading(true);

    const response = await client.conversations.appendConversationMessage({
      xOrgId: details?.orgId!,
      conversationId,
      appendConversationMessageRequest: {
        contents: [
          {
            type: "form_submission",
            result: formData,
            callId: callId,
          },
        ],
      },
    });

    processResponse(response);

    return true;
  };

  const processResponse = async (
    response: EventStream<StreamingConversationMessage>
  ): Promise<void> => {
    let message: ConversationMessage | null = null;

    for await (const event of response) {
      // This will be the first event of every response
      if (event.data.type === "metadata") {
        const metadataEvent = event.data;
        message = {
          id: event.data.id,
          contents: [],
          role: metadataEvent.role,
          author: "metadata", //TODO: Confirm this is correct
          timestamp: metadataEvent.timestamp,
        };

        setMessages((prevMessages) => [
          ...prevMessages,
          message as ConversationMessage,
        ]);
      } else if (event.data.type === "text" && message !== null) {
        // Make sure we clear loading state as soon as we get first content
        setIsLoading(false);

        const text: string = event.data.content;

        const lastContent =
          message?.contents.length > 0
            ? message?.contents[message?.contents.length - 1]
            : null;

        if (lastContent && lastContent.type === "text") {
          message.contents[message?.contents.length - 1] = {
            ...lastContent,
            content: lastContent.content + text,
          };
        } else {
          message?.contents.push({
            type: "text",
            content: text,
          });
        }

        setMessages((prevMessages) =>
          prevMessages.find((msg) => msg.id === message?.id)
            ? prevMessages.map((msg) =>
                msg.id === message?.id ? message : msg
              )
            : [...prevMessages, message as ConversationMessage]
        );
      } else if (message !== null) {
        // Also clear loading state for other message types
        setIsLoading(false);

        message.contents.push(event.data);

        setMessages((prevMessages) =>
          prevMessages.find((msg) => msg.id === message?.id)
            ? prevMessages.map((msg) =>
                msg.id === message?.id ? message : msg
              )
            : [...prevMessages, message as ConversationMessage]
        );
      }
    }

    setTimeout(sendHeight, 100);
  };

  const startNewConversation = async (
    orgDetails: GptDetails | null = details,
    preserveInput: string = cachedInputRef.current // Use the cached input as default
  ) => {
    if (!orgDetails) {
      console.error("No org details available");
      return;
    }

    setIsLoadingExisting(true);
    setMessages([]);
    // Don't clear input here, but keep track of what to restore
    const inputToRestore = preserveInput || cachedInputRef.current;

    const conversation = await client.conversations.createConversation({
      xOrgId: orgDetails.orgId,
      createConversationRequest: {
        assistantId: orgDetails.agentId,
      },
    });

    if (storeMessages) {
      window.localStorage.setItem(
        `${orgDetails.agentId}ConversationId`,
        conversation.conversationId
      );
    }

    setConversationId(conversation.conversationId);
    setHasExistingConversation(false);
    setIsLoadingExisting(false);

    // Restore the input after loading is complete
    setInputWithCache(inputToRestore);

    return conversation.conversationId;
  };

  const handleWelcomeAction = async (title: string, action: string | null) => {
    if (!action) {
      return;
    }

    const row = welcomeMessage
      .split("\n")
      .find((row) => row.includes(`t:${title}`));

    if (row && row.includes("a:")) {
      const actionBlocks = row.split("a:");
      const newAction =
        actionBlocks[0] +
        (actionBlocks[1].includes(";")
          ? actionBlocks[1].split(";")[1]
          : `]${actionBlocks[1].split("]")[1]}`);

      setWelcomeMessage(`
# ${t("youChose")}

${newAction}`);
    }

    appendMessage(action);
  };

  const getGptDetails = async () => {
    // Save current input before any state changes might reset it
    const currentInput = cachedInputRef.current;

    const tempDetails = await GetAssistantChatChannel();
    setDetails(tempDetails);

    // Check for existing conversation
    if (storeMessages) {
      const savedId = window.localStorage.getItem(
        `${tempDetails?.agentId || "gpt"}ConversationId`
      );

      if (savedId) {
        setConversationId(savedId);
        setHasExistingConversation(true);

        // Restore input after initialization
        setInputWithCache(currentInput);
      } else {
        // When starting a new conversation, pass the current input
        // so it can be restored after the conversation is created
        startNewConversation(tempDetails, currentInput);
      }
    } else {
      // When starting a new conversation, pass the current input
      // so it can be restored after the conversation is created
      startNewConversation(tempDetails, currentInput);
    }
  };

  useEffect(() => {
    getGptDetails();
    sendHeight();

    // Setup height observer
    const observer = new MutationObserver(sendHeight);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => observer.disconnect();
  }, [sendHeight]);

  useEffect(() => {
    requestAnimationFrame(sendHeight);
  }, [messages, sendHeight]);

  useEffect(() => {
    setWelcomeMessage(
      GetTextFromLangString(details?.welcomeMessage, ui.language)
    );
  }, [details, ui.language]);

  const handleFileAction = async (action: "delete" | "add", url: string) => {
    if (!conversationId || !details) {
      return;
    }

    if (action === "add") {
      setAlert({
        status: "error",
        message: "Not Yet Implemented",
        secondsToClose: 10,
      });
    }
  };

  // Modified to handle messages when not fully loaded
  const handleSubmitMessage = async (): Promise<void> => {
    if (input.trim().length === 0) return Promise.resolve();

    // Don't attempt to send if conversation is not ready - this is now handled by disabling the button
    if (!conversationId || !details || isLoadingExisting) {
      return Promise.resolve();
    }

    // Normal flow - conversation is ready
    return appendMessage(input);
  };

  // The consolidated rendering structure - no more separate loading/loaded views
  return (
    <>
      <div
        className={clsx(
          "flex flex-col",
          "w-full",
          "max-w-full",
          "h-screen relative"
        )}
      >
        {/* Message container - will scroll */}
        <div
          ref={scrollableRef}
          className={clsx(
            "w-full",
            "max-w-full",
            "flex-grow",
            "overflow-y-auto",
            "mb-[4rem]"
          )}
        >
          {/* Existing conversation prompt only when we have details */}
          {details && hasExistingConversation && (
            <div
              className={clsx(
                "p-4",
                "bg-panel",
                "rounded-xl",
                "shadow-md",
                "mb-4"
              )}
            >
              <h1 className={clsx("text-lg", "mb-4")}>
                {t("continueConversation")}
              </h1>
              <div className={clsx("grid", "md:grid-cols-2", "gap-4")}>
                <Button type="secondary" onClick={() => startNewConversation()}>
                  {ct("no")}
                </Button>
                <Button onClick={fetchExistingConversation}>{ct("yes")}</Button>
              </div>
            </div>
          )}

          {(!details || !hasExistingConversation) && (
            <>
              {/* Show AskUserInput at the top ONLY when no messages yet */}
              {(!details || messages?.length === 0) && (
                <div className="mb-8">
                  <AskUserInput
                    handleAddMessage={handleSubmitMessage}
                    disabled={!details || isLoadingExisting || !conversationId}
                    message={input}
                    setMessage={setInputWithCache}
                    fileType={FileUploadTypes.CHAT}
                    details={details || undefined}
                    conversationId={conversationId}
                    handleFileAction={handleFileAction}
                    isWingman
                    placeholder={t("homePageChatDemoPlaceholder")}
                    showInitialState={!details || messages?.length === 0}
                  />
                </div>
              )}

              {/* Conversation messages - only show if we have details */}
              {details && (
                <div
                  className={clsx({
                    "flex flex-col justify-center items-center":
                      messages?.length === 0,
                  })}
                >
                  <div className={clsx("conversation-messages mx-2")}>
                    {messages
                      .map((message) =>
                        CleanupMessages(message.contents).map((content, i) => (
                          <div
                            key={`${message.id}_${i}`}
                            className={clsx("flex", {
                              "justify-end": message.role === "user",
                              "justify-start": message.role !== "user",
                            })}
                          >
                            <AskChatBubble
                              isUser={message.role === "user"}
                              themeOverride={THEME.LIGHT}
                              brandOverride={details?.brand}
                              agentName={details?.agentName}
                              isForm={content.type === "form"}
                            >
                              {content.type === "text" && (
                                <AskText
                                  text={content.content}
                                  isMarkDown={message.role === "assistant"}
                                />
                              )}
                              {content.type === "pie_chart" && (
                                <AskPieChart
                                  chartData={content.chart}
                                  themeOverride={THEME.LIGHT}
                                  brandOverride={details?.brand}
                                />
                              )}
                              {content.type === "bar_chart" && (
                                <AskBarChart
                                  chartData={content.chart}
                                  themeOverride={THEME.LIGHT}
                                  brandOverride={details?.brand}
                                />
                              )}
                              {content.type === "form" && (
                                <AskForm
                                  callId={content.callId}
                                  schemaText={content.formSchema}
                                  countries={details.countries}
                                  submitForm={appendFormResponse}
                                />
                              )}
                              {/*content.type === "function_call" && (
                                <AskToolCall data={content} />
                              )}
                              {content.type === "function_result" && (
                                <AskToolResult data={content} />
                              )*/}
                            </AskChatBubble>
                          </div>
                        ))
                      )
                      .filter((msg) => !!msg)}
                  </div>
                </div>
              )}

              {/* Show loading indicator when appropriate - only when actively loading */}
              {isLoading && (
                <div className="mx-2">
                  <AskChatBubble
                    key="loading"
                    isUser={false}
                    themeOverride={THEME.LIGHT}
                    brandOverride={
                      details?.brand ||
                      (document
                        .querySelector("[data-brand]")
                        ?.getAttribute("data-brand") as BRANDS) ||
                      BRANDS.GAIL
                    }
                    agentName={details?.agentName}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Fixed input at the bottom of the viewport */}
        {details && messages?.length > 0 && (
          <div className="w-full absolute bottom-0 left-0">
            <AskUserInput
              handleAddMessage={handleSubmitMessage}
              disabled={isLoadingExisting || !conversationId}
              message={input}
              setMessage={setInputWithCache}
              fileType={FileUploadTypes.CHAT}
              details={details}
              conversationId={conversationId}
              handleFileAction={handleFileAction}
              isWingman
              placeholder={t("homePageChatDemoPlaceholder")}
              showInitialState={false}
            />
          </div>
        )}
      </div>
    </>
  );
}
